import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBook, IconFileText, IconX } from '@tabler/icons-react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { navigationTree, NavItem } from '../utils/docNavigation';

/**
 * Props for the DocumentationSidebar component
 */
interface DocumentationSidebarProps {
  /** Organization ID from route params */
  organizationId: string;
  /** Current active document path */
  currentDocPath?: string;
  /** Whether sidebar is open (for mobile) */
  isOpen?: boolean;
  /** Callback to close sidebar (for mobile) */
  onClose?: () => void;
}

/**
 * DocumentationSidebar component
 *
 * @description
 * Renders the documentation navigation sidebar with collapsible category sections.
 * Features include:
 * - Collapsible categories using Radix Accordion
 * - Active link highlighting
 * - Navigation to documentation pages using organization context
 * - Responsive design (always visible on desktop, toggleable on mobile)
 * - Theme-aware styling matching dashboard design
 *
 * The sidebar uses the navigation tree from docNavigation.ts and navigates to
 * `/organization/:organization_id/documentation/:docPath` routes.
 *
 * @example
 * ```tsx
 * function DocumentationPage() {
 *   const { organization_id } = useParams();
 *   const [sidebarOpen, setSidebarOpen] = useState(true);
 *
 *   return (
 *     <DocumentationSidebar
 *       organizationId={organization_id}
 *       currentDocPath="getting-started/introduction.md"
 *       isOpen={sidebarOpen}
 *       onClose={() => setSidebarOpen(false)}
 *     />
 *   );
 * }
 * ```
 */
export function DocumentationSidebar({
  organizationId: _organizationId,
  currentDocPath = '',
  isOpen = true,
  onClose,
}: DocumentationSidebarProps) {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();

  // Track which accordion items are open (all categories open by default)
  const [openCategories, setOpenCategories] = useState<string[]>(
    navigationTree
      .map((item, index) => (item.children ? `category-${index}` : null))
      .filter(Boolean) as string[],
  );

  /**
   * Navigates to a documentation page
   */
  const handleNavigation = (path: string) => {
    if (!path) return;

    // Remove .md extension for URL routing
    const cleanPath = path.replace(/\.md$/, '');
    // Use absolute path to ensure correct routing regardless of trailing slash
    navigate(`/documentation/${cleanPath}`, { replace: false });

    // Close sidebar on mobile after navigation
    if (onClose && window.innerWidth < 768) {
      onClose();
    }
  };

  /**
   * Renders a navigation link
   */
  const renderNavLink = (item: NavItem) => {
    const isActive = currentDocPath === item.path;

    return (
      <button
        key={item.path}
        type="button"
        onClick={() => handleNavigation(item.path)}
        className={cn(
          'flex items-center w-full px-3 py-2 text-sm rounded-md transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          isActive
            ? 'font-medium text-white shadow-sm'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
        )}
        style={
          isActive
            ? {
                backgroundColor: currentTheme?.primary || '#096846',
              }
            : {}
        }
        aria-current={isActive ? 'page' : undefined}
      >
        <IconFileText size={16} className="mr-2 flex-shrink-0" aria-hidden="true" />
        <span className="truncate">{item.title}</span>
      </button>
    );
  };

  /**
   * Renders a category with children using Accordion
   */
  const renderCategory = (item: NavItem, index: number) => {
    const categoryId = `category-${index}`;
    const isCategoryActive = item.path && currentDocPath === item.path;

    return (
      <AccordionItem key={categoryId} value={categoryId} className="border-none">
        <div className="flex items-center gap-1">
          {/* Category title - clickable if has path */}
          {item.path ? (
            <button
              type="button"
              onClick={() => handleNavigation(item.path)}
              className={cn(
                'flex items-center flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                'hover:bg-gray-100 dark:hover:bg-gray-800',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                isCategoryActive && 'bg-gray-100 dark:bg-gray-800',
              )}
              style={
                isCategoryActive
                  ? {
                      color: currentTheme?.primary || '#096846',
                    }
                  : {}
              }
            >
              <IconBook size={16} className="mr-2 flex-shrink-0" aria-hidden="true" />
              <span className="truncate text-left">{item.title}</span>
            </button>
          ) : (
            <div className="flex items-center flex-1 px-3 py-2 text-sm font-medium">
              <IconBook size={16} className="mr-2 flex-shrink-0" aria-hidden="true" />
              <span className="truncate text-left">{item.title}</span>
            </div>
          )}

          {/* Accordion trigger for expanding/collapsing */}
          <AccordionTrigger
            className={cn(
              'px-2 py-2 text-sm font-medium rounded-md transition-colors',
              'hover:bg-gray-100 dark:hover:bg-gray-800 hover:no-underline',
              'focus:outline-none focus:ring-2 focus:ring-offset-2',
            )}
            style={{}}
          >
            <span className="sr-only">Toggle {item.title} section</span>
          </AccordionTrigger>
        </div>
        <AccordionContent className="pb-2 pt-1">
          <div className="ml-2 space-y-1">
            {item.children?.map((child) => renderNavLink(child))}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 w-80 border-r transition-transform duration-300',
        'overflow-y-auto bg-white dark:bg-gray-950',
        isOpen ? 'translate-x-0' : '-translate-x-full',
        'md:relative md:translate-x-0',
      )}
      style={{
        borderColor: currentTheme?.['border-color'] || '#e5e7eb',
        backgroundColor: currentTheme?.background || '#ffffff',
      }}
      aria-label="Documentation navigation"
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <IconBook
              size={24}
              style={{ color: currentTheme?.primary || '#096846' }}
              aria-hidden="true"
            />
            <h2 className="text-lg font-semibold" style={{ color: currentTheme?.['primary-text'] }}>
              Documentation
            </h2>
          </div>
          {/* Close button for mobile */}
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="md:hidden"
              aria-label="Close sidebar"
            >
              <IconX size={20} />
            </Button>
          )}
        </div>

        {/* Navigation */}
        <nav aria-label="Documentation pages">
          <Accordion
            type="multiple"
            value={openCategories}
            onValueChange={setOpenCategories}
            className="space-y-1"
          >
            {navigationTree.map((item, index) => {
              // If item has children, render as category with accordion
              if (item.children && item.children.length > 0) {
                return renderCategory(item, index);
              }
              // Otherwise render as direct link
              return renderNavLink(item);
            })}
          </Accordion>
        </nav>
      </div>
    </aside>
  );
}
